import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ComplexHeatingPage = () => {
  const heatingSystems = [
    {
      title: 'Central Heating Systems',
      description: 'Complete central heating installation with modern boilers and radiators throughout your home.',
      features: [
        'High-efficiency condensing boilers',
        'Smart thermostatic controls',
        'Energy-efficient radiators',
        'Zoned heating options',
        'Professional system design',
      ],
      image: 'https://public.readdy.ai/ai/img_res/b795590af9856174c6685cf13b74e45a.jpg',
    },
    {
      title: 'Underfloor Heating',
      description: 'Luxurious underfloor heating systems providing even warmth and maximum comfort.',
      features: [
        'Electric and water-based systems',
        'Compatible with all floor types',
        'Energy-efficient operation',
        'Individual room control',
        'Long-term reliability',
      ],
      image: 'https://readdy.ai/api/search-image?query=underfloor%20heating%20installation%20showing%20heating%20pipes%20laid%20on%20floor%20modern%20system%20professional%20work%20clean%20organized%20setup%20light%20concrete%20floor&width=800&height=600&seq=heat-sys-002&orientation=landscape',
    },
    {
      title: 'Heat Pump Systems',
      description: 'Eco-friendly heat pump installations for sustainable and cost-effective heating.',
      features: [
        'Air-to-water heat pumps',
        'Ground source options',
        'Low running costs',
        'Renewable energy solution',
        'Government grant eligible',
      ],
      image: 'https://readdy.ai/api/search-image?query=modern%20heat%20pump%20system%20outdoor%20unit%20mounted%20on%20wall%20energy%20efficient%20heating%20solution%20professional%20installation%20clean%20setup%20light%20background&width=800&height=600&seq=heat-sys-003&orientation=landscape',
    },
  ];

  const benefits = [
    {
      icon: 'ri-leaf-line',
      title: 'Energy Efficient',
      description: 'Reduce your energy bills with modern, efficient heating systems',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Reliable Performance',
      description: 'Quality installations backed by comprehensive warranties',
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Smart Controls',
      description: 'Advanced thermostats and zoning for optimal comfort',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Expert Support',
      description: '24/7 emergency service and ongoing maintenance',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center">
          <div className="absolute inset-0 z-0">
            <img
              src="https://readdy.ai/api/search-image?query=modern%20home%20heating%20system%20installation%20contemporary%20Irish%20house%20interior%20with%20radiators%20and%20heating%20controls%20warm%20comfortable%20living%20space%20professional%20setup%20light%20neutral%20tones&width=1920&height=1080&seq=heat-hero-001&orientation=landscape"
              alt="Complex Heating Systems"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
          </div>

          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="max-w-3xl">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                Complex Heating Solutions
              </h1>
              <p className="text-lg sm:text-xl text-gray-200 leading-relaxed mb-8">
                Professional installation and maintenance of advanced heating systems. From traditional central heating to modern heat pumps, we deliver comfort and efficiency.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#systems"
                  className="inline-flex items-center justify-center space-x-2 bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-lg font-medium transition-colors whitespace-nowrap"
                >
                  <span>Explore Systems</span>
                  <i className="ri-arrow-down-line"></i>
                </a>
                <a
                  href="/#contact"
                  className="inline-flex items-center justify-center space-x-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary px-8 py-4 rounded-lg font-medium transition-colors whitespace-nowrap"
                >
                  <span>Get Quote</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Heating Systems Section */}
        <section id="systems" className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-4">
                Our Heating Systems
              </h2>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                Choose from our range of professional heating solutions designed for Irish homes
              </p>
            </div>

            <div className="space-y-16">
              {heatingSystems.map((system, index) => (
                <div
                  key={index}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="aspect-[4/3] w-full h-full rounded-2xl overflow-hidden shadow-xl">
                      <img
                        src={system.image}
                        alt={system.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <h3 className="text-3xl font-bold text-primary mb-4">
                      {system.title}
                    </h3>
                    <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                      {system.description}
                    </p>
                    <ul className="space-y-3 mb-8">
                      {system.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <i className="ri-check-line text-accent text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <a
                      href="/#contact"
                      className="inline-flex items-center space-x-2 text-accent hover:text-accent-dark font-semibold transition-colors whitespace-nowrap"
                    >
                      <span>Request Information</span>
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-primary mb-4">
                Why Choose Our Heating Solutions?
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Experience the difference with professional installation and ongoing support
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-8 text-center hover:shadow-lg transition-shadow"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-accent/10 rounded-full mx-auto mb-6">
                    <i className={`${benefit.icon} text-3xl text-accent`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-primary mb-6">
              Ready to Upgrade Your Heating?
            </h2>
            <p className="text-gray-600 text-lg mb-8">
              Contact our heating experts today for a free consultation and personalized quote for your home.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/#contact"
                className="inline-flex items-center justify-center space-x-2 bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-lg font-medium transition-colors whitespace-nowrap"
              >
                <span>Get Free Quote</span>
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="tel:+353873719751"
                className="inline-flex items-center justify-center space-x-2 bg-white border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-4 rounded-lg font-medium transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                <span>+353 87 371 9751</span>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ComplexHeatingPage;